// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — Single Source of Truth
// All raw values live here. Nothing is hardcoded anywhere else.
// ─────────────────────────────────────────────────────────────────────────────

// ── Palette (Primitives) ──────────────────────────────────────────────────────

export const Palette = {
  // Absolute
  white:     '#ffffff',
  black:     '#000000',

  // Pure Grayscale (0% saturation)
  gray50:    '#f9f9f9',
  gray100:   '#f1f1f1',
  gray200:   '#e6e6e6',
  gray300:   '#d1d1d1',
  gray400:   '#b3b3b3',
  gray500:   '#838383',
  gray600:   '#676767',
  gray700:   '#4d4d4d',
  gray800:   '#2a2a2a',
  gray900:   '#161616',
  gray950:   '#0d0d0d',
} as const;

// ── Shadows ───────────────────────────────────────────────────────────────────

export const Shadows = {
  card: {
    shadowColor:   '#000000',
    shadowOffset:  { width: 0, height: 12 },
    shadowOpacity: 0.1,
    shadowRadius:  12,
    elevation:     8,
  },
  subtle: {
    shadowColor:   '#000000',
    shadowOffset:  { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius:  8,
    elevation:     2,
  },
} as const;

// ── Semantic Color Tokens ─────────────────────────────────────────────────────

export const Colors = {
  // Brand (Pure Monochrome primary)
  brand:          Palette.gray900,
  brandHover:     Palette.gray800,
  brandSubtle:    Palette.gray100,

  // Status (Monochrome high-contrast)
  success:        Palette.gray900,
  successActive:  Palette.black,
  danger:         Palette.gray700,
  warning:        Palette.gray600,
  upsell:         Palette.black,

  // Absolute
  white:          Palette.white,
  black:          Palette.black,

  // Light mode
  bgLight:        Palette.gray50,
  surfaceLight:   Palette.white,
  borderLight:    Palette.gray200,
  textLight:      Palette.gray900,
  subtextLight:   Palette.gray500,
  pillLight:      Palette.gray100,

  // Dark mode
  bgDark:         Palette.gray900,
  surfaceDark:    Palette.black, // or gray950 for elevated contrast
  borderDark:     Palette.gray800,
  textDark:       Palette.gray50,
  subtextDark:    Palette.gray400, // Slightly lighter for legibility on dark
  pillDark:       Palette.gray800,

  // Monochromatic Solid Wallpapers (Removed noisy gradients for brutalist feel)
  wallpapers: [
    Palette.gray900,
    Palette.gray800,
    Palette.gray950,
    Palette.black,
  ] as const,
} as const;

// ── Theme resolver ────────────────────────────────────────────────────────────

export type ColorMode = 'light' | 'dark';

export const getThemeColors = (mode: ColorMode) => ({
  bg:            mode === 'dark' ? Colors.bgDark        : Colors.bgLight,
  surface:       mode === 'dark' ? Colors.surfaceDark   : Colors.surfaceLight,
  border:        mode === 'dark' ? Colors.borderDark    : Colors.borderLight,
  text:          mode === 'dark' ? Colors.textDark      : Colors.textLight,
  subtext:       mode === 'dark' ? Colors.subtextDark   : Colors.subtextLight,
  pill:          mode === 'dark' ? Colors.pillDark      : Colors.pillLight,
  cardTint:      mode === 'dark' ? Colors.pillDark      : Colors.brandSubtle,
  
  // Button & Accents (Inverted)
  brand:         mode === 'dark' ? Palette.gray50       : Palette.gray900,
  brandText:     mode === 'dark' ? Palette.gray900      : Palette.gray50,

  // kept for backward-compat aliases
  textPrimary:   mode === 'dark' ? Colors.textDark      : Colors.textLight,
  textSecondary: mode === 'dark' ? Colors.subtextDark   : Colors.subtextLight,
  accent:        mode === 'dark' ? Palette.gray50       : Palette.gray900,
  success:       mode === 'dark' ? Palette.gray50       : Palette.gray900,
  danger:        mode === 'dark' ? Palette.gray400      : Palette.gray600,
  warning:       mode === 'dark' ? Palette.gray400      : Palette.gray600,
});

export type ThemeColors = ReturnType<typeof getThemeColors>;

// ── Spacing scale (4-pt grid) ─────────────────────────────────────────────────

export const Spacing = {
  px:  1,
  xs:  4,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  20,
  xxl: 24,
  xxxl:40,
  huge:48,
  gargantuan:120,
} as const;

// ── Border radii ───────────────────────────────────────────────────────────────

export const Radius = {
  xs:    2,
  sm:    4,
  md:    8,
  lg:    12, // Standard Card
  xl:    16,
  xxl:   24, // WeTransfer-style floating card
  pill:  48, // New Pill Button Standard
  full:  9999,
} as const;

// ── Typography ────────────────────────────────────────────────────────────────

export const FontSize = {
  xs:   11, // Metadata / Tiny labels
  sm:   13, // Secondary text
  md:   14, // Body / Standard buttons
  lg:   16, // Subheadings
  xl:   20, // H3
  xxl:  28, // H2
  xxxl: 36, // H1
  hero: 56, // Display
} as const;

export const FontWeight = {
  regular: '400' as const,
  medium:  '500' as const,
  semibold:'600' as const,
  bold:    '700' as const,
  black:   '900' as const,
};

// ── Breakpoints (px) - Industry Standard (Tailwind defaults) ───────────────────

export const Breakpoints = {
  sm:  640,
  md:  768,
  lg:  1024,
  xl:  1280,
} as const;

// ── Layout constants ──────────────────────────────────────────────────────────

export const Layout = {
  sidebarWidth:  85,
  cardWidth:     360,
  maxContentWidth: 1342,
} as const;

// ── Backward-compat aliases ───────────────────────────────────────────────────
// Prevent breaking existing imports while we migrate.

export const Border = {
  radiusSm:     Radius.sm,
  radiusMd:     Radius.md,
  radiusLg:     Radius.lg,
  radiusXl:     Radius.xl,
  radiusRound:  Radius.full,
};

export const Typography = {
  sizeXs:       FontSize.xs,
  sizeSm:       FontSize.sm,
  sizeMd:       FontSize.md,
  sizeLg:       FontSize.lg,
  sizeXl:       FontSize.xl,
  sizeXxl:      FontSize.xxl,
  weightRegular: FontWeight.regular,
  weightMedium:  FontWeight.medium,
  weightBold:    FontWeight.bold,
};
